import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  BookOpen, 
  Video,
  FileText,
  GraduationCap,
  Target,
  TrendingUp,
  Users,
  Star,
  ChevronLeft,
  Eye,
  EyeOff,
  GripVertical
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: "BookOpen", label: "Book", icon: BookOpen },
  { value: "GraduationCap", label: "Graduation", icon: GraduationCap },
  { value: "Target", label: "Target", icon: Target },
  { value: "TrendingUp", label: "Growth", icon: TrendingUp },
  { value: "Users", label: "Users", icon: Users },
  { value: "Video", label: "Video", icon: Video },
  { value: "FileText", label: "Document", icon: FileText },
  { value: "Star", label: "Star", icon: Star },
];

export default function LearningAdminPage({ user }) {
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("courses");
  const navigate = useNavigate();

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedCourseForLesson, setSelectedCourseForLesson] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", icon: "BookOpen" });
  const [courseForm, setCourseForm] = useState({
    title: "", description: "", category_id: "", difficulty: "beginner",
    thumbnail_url: "", is_published: false
  });
  const [lessonForm, setLessonForm] = useState({
    title: "", description: "", content_type: "video", video_url: "",
    text_content: "", duration_minutes: 0, is_published: false
  });

  useEffect(() => {
    // Redirect if not platform owner
    if (user && user.system_role !== "platform_owner") {
      navigate("/learning");
      toast.error("Access denied. Platform owner only.");
    } else {
      fetchData();
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [catRes, courseRes] = await Promise.all([
        fetch(`${API}/learning/categories`, { credentials: "include" }),
        fetch(`${API}/learning/courses?include_unpublished=true`, { credentials: "include" })
      ]);

      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }

      if (courseRes.ok) {
        const courseData = await courseRes.json();
        // Fetch lessons for each course
        const coursesWithLessons = await Promise.all(
          (courseData.courses || []).map(async (course) => {
            const lessonRes = await fetch(
              `${API}/learning/courses/${course.course_id}`,
              { credentials: "include" }
            );
            if (lessonRes.ok) {
              const data = await lessonRes.json();
              return { ...course, lessons: data.lessons || [] };
            }
            return { ...course, lessons: [] };
          })
        );
        setCourses(coursesWithLessons);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || "",
        icon: category.icon || "BookOpen"
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", icon: "BookOpen" });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      const url = editingCategory
        ? `${API}/learning/categories/${editingCategory.category_id}`
        : `${API}/learning/categories`;
      
      const response = await fetch(url, {
        method: editingCategory ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        toast.success(editingCategory ? "Category updated" : "Category created");
        setCategoryDialogOpen(false);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save category");
      }
    } catch (error) {
      toast.error("Failed to save category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category? Courses must be moved first.")) return;

    try {
      const response = await fetch(`${API}/learning/categories/${categoryId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Category deleted");
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete category");
      }
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  // Course handlers
  const openCourseDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setCourseForm({
        title: course.title,
        description: course.description || "",
        category_id: course.category_id || "",
        difficulty: course.difficulty || "beginner",
        thumbnail_url: course.thumbnail_url || "",
        is_published: course.is_published || false
      });
    } else {
      setEditingCourse(null);
      setCourseForm({
        title: "", description: "", category_id: categories[0]?.category_id || "",
        difficulty: "beginner", thumbnail_url: "", is_published: false
      });
    }
    setCourseDialogOpen(true);
  };

  const handleSaveCourse = async () => {
    try {
      const url = editingCourse
        ? `${API}/learning/courses/${editingCourse.course_id}`
        : `${API}/learning/courses`;
      
      const response = await fetch(url, {
        method: editingCourse ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(courseForm)
      });

      if (response.ok) {
        toast.success(editingCourse ? "Course updated" : "Course created");
        setCourseDialogOpen(false);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save course");
      }
    } catch (error) {
      toast.error("Failed to save course");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Delete this course and all its lessons?")) return;

    try {
      const response = await fetch(`${API}/learning/courses/${courseId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Course deleted");
        fetchData();
      } else {
        toast.error("Failed to delete course");
      }
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  // Lesson handlers
  const openLessonDialog = (course, lesson = null) => {
    setSelectedCourseForLesson(course);
    if (lesson) {
      setEditingLesson(lesson);
      setLessonForm({
        title: lesson.title,
        description: lesson.description || "",
        content_type: lesson.content_type || "video",
        video_url: lesson.video_url || "",
        text_content: lesson.text_content || "",
        duration_minutes: lesson.duration_minutes || 0,
        is_published: lesson.is_published || false
      });
    } else {
      setEditingLesson(null);
      setLessonForm({
        title: "", description: "", content_type: "video",
        video_url: "", text_content: "", duration_minutes: 0, is_published: false
      });
    }
    setLessonDialogOpen(true);
  };

  const handleSaveLesson = async () => {
    try {
      const url = editingLesson
        ? `${API}/learning/lessons/${editingLesson.lesson_id}`
        : `${API}/learning/courses/${selectedCourseForLesson.course_id}/lessons`;
      
      const response = await fetch(url, {
        method: editingLesson ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(lessonForm)
      });

      if (response.ok) {
        toast.success(editingLesson ? "Lesson updated" : "Lesson created");
        setLessonDialogOpen(false);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save lesson");
      }
    } catch (error) {
      toast.error("Failed to save lesson");
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;

    try {
      const response = await fetch(`${API}/learning/lessons/${lessonId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Lesson deleted");
        fetchData();
      } else {
        toast.error("Failed to delete lesson");
      }
    } catch (error) {
      toast.error("Failed to delete lesson");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="learning-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/learning")}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Learning Center Admin</h1>
            <p className="text-slate-500">Manage categories, courses, and lessons</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="courses">Courses & Lessons</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Courses Tab */}
        <TabsContent value="courses" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openCourseDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              New Course
            </Button>
          </div>

          {courses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium mb-2">No Courses Yet</h3>
                <p className="text-slate-500 mb-4">Create your first course to get started</p>
                <Button onClick={() => openCourseDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Course
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="multiple" className="space-y-4">
              {courses.map((course) => (
                <AccordionItem key={course.course_id} value={course.course_id} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3 flex-1 text-left">
                      <div className="w-16 h-10 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{course.title}</span>
                          {course.is_published ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Published</Badge>
                          ) : (
                            <Badge variant="outline">Draft</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {course.lessons?.length || 0} lessons
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      {/* Course Actions */}
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openCourseDialog(course)}>
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit Course
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openLessonDialog(course)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Add Lesson
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteCourse(course.course_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Lessons List */}
                      <div className="border rounded-lg divide-y">
                        {course.lessons?.map((lesson, index) => (
                          <div 
                            key={lesson.lesson_id}
                            className="flex items-center gap-3 p-3 hover:bg-slate-50"
                          >
                            <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {lesson.content_type === "video" ? (
                                  <Video className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <FileText className="w-4 h-4 text-slate-400" />
                                )}
                                <span className="truncate">{lesson.title}</span>
                                {lesson.is_published ? (
                                  <Eye className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <EyeOff className="w-4 h-4 text-slate-300" />
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openLessonDialog(course, lesson)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600"
                                onClick={() => handleDeleteLesson(lesson.lesson_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {(!course.lessons || course.lessons.length === 0) && (
                          <div className="p-6 text-center text-slate-500">
                            No lessons yet. Add your first lesson.
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openCategoryDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              New Category
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => {
              const IconComponent = iconOptions.find(i => i.value === category.icon)?.icon || BookOpen;
              return (
                <Card key={category.category_id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium">{category.name}</h3>
                          <p className="text-sm text-slate-500">{category.course_count || 0} courses</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(category)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeleteCategory(category.category_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {category.description && (
                      <p className="text-sm text-slate-500 mt-2">{category.description}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {categories.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium mb-2">No Categories Yet</h3>
                  <p className="text-slate-500 mb-4">Create categories to organize your courses</p>
                  <Button onClick={() => openCategoryDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Category
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription>
              Categories help organize your courses into topics
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Sales Techniques"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <Select 
                value={categoryForm.icon} 
                onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory}>Save Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Dialog */}
      <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCourse ? "Edit Course" : "New Course"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Course Title</Label>
              <Input
                value={courseForm.title}
                onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                placeholder="e.g., Cold Calling Mastery"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                placeholder="What will students learn in this course?"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={courseForm.category_id} 
                  onValueChange={(v) => setCourseForm({ ...courseForm, category_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Difficulty</Label>
                <Select 
                  value={courseForm.difficulty} 
                  onValueChange={(v) => setCourseForm({ ...courseForm, difficulty: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Thumbnail URL (optional)</Label>
              <Input
                value={courseForm.thumbnail_url}
                onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Published</Label>
                <p className="text-sm text-slate-500">Make visible to all users</p>
              </div>
              <Switch
                checked={courseForm.is_published}
                onCheckedChange={(v) => setCourseForm({ ...courseForm, is_published: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCourse}>Save Course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLesson ? "Edit Lesson" : "New Lesson"}</DialogTitle>
            {selectedCourseForLesson && (
              <DialogDescription>
                Adding to: {selectedCourseForLesson.title}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Lesson Title</Label>
              <Input
                value={lessonForm.title}
                onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                placeholder="e.g., Introduction to Cold Calling"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={lessonForm.description}
                onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                placeholder="Brief overview of this lesson"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select 
                  value={lessonForm.content_type} 
                  onValueChange={(v) => setLessonForm({ ...lessonForm, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="text">Text/Article</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={lessonForm.duration_minutes}
                  onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>

            {lessonForm.content_type === "video" && (
              <div className="space-y-2">
                <Label>Video URL</Label>
                <Input
                  value={lessonForm.video_url}
                  onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                  placeholder="YouTube, Vimeo, or direct video URL"
                />
                <p className="text-xs text-slate-500">Supports YouTube, Vimeo, or direct video file URLs</p>
              </div>
            )}

            {lessonForm.content_type === "text" && (
              <div className="space-y-2">
                <Label>Content (Markdown supported)</Label>
                <Textarea
                  value={lessonForm.text_content}
                  onChange={(e) => setLessonForm({ ...lessonForm, text_content: e.target.value })}
                  placeholder="Write your lesson content here..."
                  rows={8}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Label>Published</Label>
                <p className="text-sm text-slate-500">Make visible to users</p>
              </div>
              <Switch
                checked={lessonForm.is_published}
                onCheckedChange={(v) => setLessonForm({ ...lessonForm, is_published: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLesson}>Save Lesson</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
